import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";

import "./styles.scss";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import {
    CommunityAssetImageField,
    AssetCategory,
    isValidHttpUrl,
} from "@features/uploadAsset";

import { Input } from "@fragments";

function extractPlaylistEntity(res) {
    const status = res?.status ?? res?.response?.status;
    if (status != null && status >= 400) return null;
    const body = res?.data;
    if (!body) return null;
    return body.data ?? body;
}

export default function ConfigPlaylistModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadBusyCount, setUploadBusyCount] = useState(0);
    const [, setTick] = useState(0);

    const REF_config = useRef({});
    const REF_callback = useRef();

    const bump = () => setTick((n) => n + 1);

    const handleUploadingChange = (uploading) => {
        setUploadBusyCount((c) => Math.max(0, c + (uploading ? 1 : -1)));
    };

    modalRef.current = {
        open(preFill, callback) {
            const rawBanner = preFill.banner;
            const trimmed =
                rawBanner != null ? String(rawBanner).trim() : "";
            const bannerUrl = trimmed && isValidHttpUrl(trimmed) ? trimmed : null;

            REF_config.current = {
                ...preFill,
                name: preFill.name ?? "",
                description: preFill.description ?? "",
                banner: bannerUrl,
                section: preFill.section,
                order: preFill.order,
                id: preFill.id,
                community: preFill.community,
            };
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    async function handleSubmit() {
        try {
            setLoading(true);
            const payload = getPayload();
            // Section ids are hashed slugs (`sc-…`); the API resolves them itself,
            // so they must be sent as-is (parseInt → NaN → /section/NaN/playlist).
            const sectionId = REF_config.current.section;

            let res;
            if (REF_config.current.id) {
                res = await api.patch(
                    `section/playlist/${REF_config.current.id}`,
                    payload
                );
            } else {
                res = await api.post(`section/${sectionId}/playlist`, payload);
            }

            const data = extractPlaylistEntity(res);

            if (data?.id) {
                await REF_callback.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);

        function getPayload() {
            const { name, description, banner, order } = REF_config.current;

            const data = {
                name: name.trim(),
                description: description?.trim() ?? null,
                order: parseInt(order, 10) || 0,
            };

            if (banner && isValidHttpUrl(banner)) {
                data.banner = banner;
            }

            return data;
        }
    }

    async function handleDelete() {
        try {
            await api.delete(`section/playlist/${REF_config.current.id}`);
            REF_callback.current();
        } catch (e) {
            console.log(e);
        }

        setOpen(false);
    }

    const communityId = REF_config.current.community
        ? parseInt(REF_config.current.community, 10)
        : null;

    return (
        <CustomModal
            maxWidth={450}
            bottomButtonSumbitText={t("managePlaylist.submitButton")}
            bottomButtonCancelText={t("common:actions.cancel")}
            loading={loading}
            disabled={uploadBusyCount > 0}
            handleDelete={REF_config.current?.id && handleDelete}
            deletionTitle={t("managePlaylist.deleteConfirmTitle")}
            title={t("managePlaylist.modalTitle")}
            handleSubmit={handleSubmit}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open && communityId ? (
                    <div className="px-4 pb-1 py-3">
                        <CommunityAssetImageField
                            communityId={communityId}
                            category={AssetCategory.PLAYLIST_BANNER}
                            value={REF_config.current.banner}
                            onChange={(url) => {
                                REF_config.current.banner = url;
                                bump();
                            }}
                            onUploadingChange={handleUploadingChange}
                            aspectRatio={4 / 3}
                            size={120}
                            imageMaxWidth={1600}
                            title={t("managePlaylist.bannerCropTitle")}
                            className="playlist-banner-input"
                        />
                        <Input.Text
                            className="mt-4"
                            autoFocus={true}
                            placeholder={t("managePlaylist.namePlaceholder")}
                            value={REF_config.current.name}
                            onInput={(v) => {
                                REF_config.current.name = v;
                            }}
                            label={t("managePlaylist.nameLabel")}
                            size={50}
                        />
                        <Input.Text
                            className="mt-2"
                            multiline
                            placeholder={t("managePlaylist.descriptionPlaceholder")}
                            value={REF_config.current.description}
                            onInput={(v) => {
                                REF_config.current.description = v;
                            }}
                            label={t("managePlaylist.descriptionLabel")}
                            size={250}
                        />
                    </div>
                ) : null
            }
        />
    );
}
